"use client";
import React, { useEffect, useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { Box, Typography, CircularProgress, TextField, Button } from "@mui/material";

const MEETING_TIMEZONE = "Asia/Karachi";

const formatMeetingTime = (iso) =>
    new Date(iso).toLocaleString(undefined, {
        timeZone: MEETING_TIMEZONE,
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });

// ── IMPORTANT: Shell is defined OUTSIDE the page component (module
// scope), not inside it. If it were declared inside CancelMeetingContent,
// every re-render (e.g. every keystroke in the reason TextField, since
// that updates state) would create a BRAND NEW Shell function. React
// would then treat it as a completely different component type and
// unmount/remount the whole subtree — including the TextField — which
// is exactly what was stealing focus after every single character. ──
const Shell = ({ children }) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            px: 2,
            bgcolor: "#F5F5F7",
        }}
    >
        <Box
            sx={{
                bgcolor: "#fff",
                borderRadius: "12px",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                p: { xs: 3.5, sm: 5 },
                maxWidth: 530,
                width: "100%",
                textAlign: "center",
            }}
        >
            {children}
        </Box>
    </Box>
);

// ── Renamed from CancelMeetingPage. useSearchParams() requires this
// component to be wrapped in a <Suspense> boundary (Next.js will fail
// the build otherwise, since this can bail out to client-side
// rendering). All original logic below is unchanged. ──
function CancelMeetingContent() {
    const searchParams = useSearchParams();
    const submissionId = searchParams.get("submissionId");

    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [reason, setReason] = useState("");
    const [cancelling, setCancelling] = useState(false);
    const [cancelled, setCancelled] = useState(false);

    useEffect(() => {
        if (!submissionId) {
            setError("This link is missing some information. Please use the link from your confirmation email.");
            setLoading(false);
            return;
        }

        let cancelledFlag = false;
        setLoading(true);
        setError("");

        fetch(`/api/google/booking-info?submissionId=${encodeURIComponent(submissionId)}`)
            .then((r) => r.json())
            .then((data) => {
                if (cancelledFlag) return;
                if (!data.success) {
                    setError(data.message || "Booking not found.");
                } else if (data.meetingCancelled) {
                    setCancelled(true); // already cancelled — seedha "cancelled" state dikhado
                    setInfo(data);
                } else {
                    setInfo(data);
                }
            })
            .catch(() => {
                if (!cancelledFlag) setError("Something went wrong. Please try again.");
            })
            .finally(() => {
                if (!cancelledFlag) setLoading(false);
            });

        return () => {
            cancelledFlag = true;
        };
    }, [submissionId]);

    const handleConfirmCancel = async () => {
        setCancelling(true);
        setError("");
        try {
            const res = await fetch("/api/google/cancel-confirm", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ submissionId, reason }),
            });
            const data = await res.json();
            if (!data.success) {
                setError(data.message || "Could not cancel the meeting. Please try again.");
                setCancelling(false);
                return;
            }
            setCancelled(true);
        } catch {
            setError("Could not cancel the meeting. Please try again.");
        } finally {
            setCancelling(false);
        }
    };

    if (loading) {
        return (
            <Shell>
                <CircularProgress sx={{ color: "#7C3AED" }} />
            </Shell>
        );
    }

    if (error && !info) {
        return (
            <Shell>
                <Typography
                    sx={{
                        fontFamily: "var(--font-plusjakarta), sans-serif",
                        fontWeight: 700,
                        fontSize: "1.2rem",
                        color: "#111827",
                        mb: 1.5,
                    }}
                >
                    Something&apos;s not right
                </Typography>
                <Typography sx={{ fontFamily: "var(--font-outfit), sans-serif", color: "#4B5563", fontSize: "0.95rem" }}>
                    {error}
                </Typography>
            </Shell>
        );
    }

    // ── Already cancelled OR just got cancelled — same success card ──
    if (cancelled) {
        return (
            <Shell>
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        background: "#7C3AED1A",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2.5,
                    }}
                >
                    <Typography sx={{ fontSize: "26px", color: "#7C3AED" }}>✓</Typography>
                </Box>
                <Typography
                    sx={{
                        fontFamily: "var(--font-plusjakarta), sans-serif",
                        fontWeight: 700,
                        fontSize: "1.2rem",
                        color: "#111827",
                        mb: 1.25,
                    }}
                >
                    {info?.meetingCancelled && !cancelling ? "Already cancelled" : "Meeting cancelled"}
                </Typography>
                <Typography sx={{ fontFamily: "var(--font-outfit), sans-serif", color: "#4B5563", fontSize: "0.95rem", lineHeight: 1.6 }}>
                    Your meeting has been cancelled and removed from the calendar. Feel free to book a new time whenever you&apos;re ready.
                </Typography>

            </Shell>
        );
    }

    // ── Confirmation step — naam/email/time dikhao, optional reason lo,
    // aur sirf button dabane par cancel karo ──
    const timeLabel = info?.meetingStartTime ? formatMeetingTime(info.meetingStartTime) : null;

    return (
        <Shell>
            <Typography
                sx={{
                    fontFamily: "var(--font-plusjakarta), sans-serif",
                    fontWeight: 700,
                    fontSize: "1.25rem",
                    color: "#111827",
                    mb: 1,
                }}
            >
                Cancel this meeting?
            </Typography>
            <Typography sx={{ fontFamily: "var(--font-outfit), sans-serif", color: "#6B7280", fontSize: "0.9rem", mb: 3 }}>
                Please review the details below before cancelling.
            </Typography>

            <Box
                sx={{
                    bgcolor: "#F9FAFB",
                    borderRadius: "10px",
                    p: 2.5,
                    mb: 3,
                    textAlign: "left",
                }}
            >
                {info?.candidateName && (
                    <Typography sx={{ fontFamily: "var(--font-outfit), sans-serif", fontSize: "0.88rem", color: "#374151", mb: 0.75 }}>
                        <strong>Name:</strong> {info.candidateName}
                    </Typography>
                )}
                {info?.candidateEmail && (
                    <Typography sx={{ fontFamily: "var(--font-outfit), sans-serif", fontSize: "0.88rem", color: "#374151", mb: 0.75 }}>
                        <strong>Email:</strong> {info.candidateEmail}
                    </Typography>
                )}
                {timeLabel && (
                    <Typography sx={{ fontFamily: "var(--font-outfit), sans-serif", fontSize: "0.88rem", color: "#374151" }}>
                        <strong>Scheduled for:</strong> {timeLabel}
                    </Typography>
                )}
            </Box>

            <Box sx={{ textAlign: "left", mb: 3 }}>
                <Typography
                    sx={{
                        fontFamily: "var(--font-outfit), sans-serif",
                        fontSize: "0.85rem",
                        fontWeight: 500,
                        color: "#374151",
                        mb: 0.75,
                    }}
                >
                    Reason for cancelling <Box component="span" sx={{ color: "#9CA3AF" }}>(optional)</Box>
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Let us know why you're cancelling…"
                    disabled={cancelling}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontFamily: "var(--font-outfit), sans-serif",
                            fontSize: "0.9rem",
                        },
                    }}
                />
            </Box>

            {error && (
                <Typography sx={{ color: "#EF4444", fontSize: "0.85rem", mb: 2 }}>{error}</Typography>
            )}

            <Button
                onClick={handleConfirmCancel}
                disabled={cancelling}
                variant="contained"
                fullWidth
                sx={{
                    bgcolor: "#EF4444",
                    textTransform: "none",
                    fontFamily: "var(--font-outfit), sans-serif",
                    fontWeight: 600,
                    borderRadius: "8px",
                    py: 1.1,
                    "&:hover": { bgcolor: "#DC2626" },
                }}
            >
                {cancelling ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Cancel Meeting"}
            </Button>
        </Shell>
    );
}

// ── New default export. Wraps CancelMeetingContent in a Suspense
// boundary so `next build` no longer throws the
// "useSearchParams() should be wrapped in a suspense boundary" error. ──
export default function CancelMeetingPage() {
    return (
        <Suspense
            fallback={
                <Shell>
                    <CircularProgress sx={{ color: "#7C3AED" }} />
                </Shell>
            }
        >
            <CancelMeetingContent />
        </Suspense>
    );
}